import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { useBackdropContext } from '../../../context/backdrop';
import {
  ChangeUserRoleMutation,
  ChangeUserRoleMutationVariables,
  RemoveUserFromInstallationMutation,
  RemoveUserFromInstallationMutationVariables,
  UserSharingInfoQuery,
  UserSharingInfoQueryVariables,
} from '../../../data-access/gql-types/graphql';
import { REMOVE_USER_FROM_INSTALLATION } from '../../../data-access/mutations/installations';
import { CHANGE_USER_ROLE } from '../../../data-access/mutations/share';
import { USER_SHARING_INFO } from '../../../data-access/queries/share';
import { useMutationErrors } from '../../../hooks/errors/use-mutation-errors';
import { useUserProfileStore } from '../../../store/user-profile';
import { MutationErrorType } from '../../../types';
import { toastError, toastSuccess } from '../../../utils/toast';
import { useShareInstallationContext } from '../context';

export const useMember = (fetchUserSharing?: boolean) => {
  const navigate = useNavigate();
  const { t } = useTranslation('installation');
  const { t: tc } = useTranslation('common');
  const { state } = useShareInstallationContext();
  const { handleErrors } = useMutationErrors(MutationErrorType.Invitation);
  const refetchUserMe = useUserProfileStore((s) => s.refetchUserMe);
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const {
    data: userSharing,
    loading: userSharingLoading,
    refetch: refetchSharingInfo,
  } = useQuery<UserSharingInfoQuery, UserSharingInfoQueryVariables>(USER_SHARING_INFO, {
    variables: { userId: state.userId, installationId: state.installationId },
    fetchPolicy: 'no-cache',
    skip: !state.installationId || !state.userId || !fetchUserSharing,
  });
  const [changeUserRole, { loading: changeRoleLoading }] = useMutation<
    ChangeUserRoleMutation,
    ChangeUserRoleMutationVariables
  >(CHANGE_USER_ROLE);
  const [removeUserFromInstallation, { loading: removeUserFromInstallationLoading }] = useMutation<
    RemoveUserFromInstallationMutation,
    RemoveUserFromInstallationMutationVariables
  >(REMOVE_USER_FROM_INSTALLATION);

  const changeRole = () => {
    turnOnBackdrop();
    changeUserRole({
      variables: {
        input: {
          userId: state.userId,
          installationId: state.installationId,
          accessType: state.permission,
        },
      },
      onCompleted: (data) => {
        turnOffBackdrop();
        if (!data || !data.changeUserRole) {
          toastError({ content: tc('errors.somethingWentWrong') });
          return;
        }

        if (data.changeUserRole?.result?.ok) {
          toastSuccess({ content: t('shared_user.change_role_success') });
          refetchUserMe();
          navigate(-1);
        } else {
          if (data.changeUserRole?.errors) {
            handleErrors(data.changeUserRole.errors || []);
          }
        }
      },
      onError: () => turnOffBackdrop(),
    });
  };

  const deleteUserFromInstallation = (userId: string, installationId: string, callback: () => void) => {
    turnOnBackdrop();
    removeUserFromInstallation({
      variables: {
        input: {
          userId,
          installationId,
        },
      },
      onCompleted: (data) => {
        turnOffBackdrop();
        callback();
        if (!data || !data.removeUserFromInstallation) {
          toastError({ content: tc('errors.somethingWentWrong') });
          return;
        }

        if (data.removeUserFromInstallation.result?.ok) {
          toastSuccess({ content: t('shared_user.remove_user_success') });
        } else {
          handleErrors(data.removeUserFromInstallation.errors || []);
        }
      },
      onError: () => turnOffBackdrop(),
    });
  };

  return {
    changeRole,
    changeRoleLoading,
    deleteUserFromInstallation,
    removeUserFromInstallationLoading,
    userSharing,
    userSharingLoading,
    refetchSharingInfo,
  };
};
